import React, { useEffect, useRef, useState } from 'react';
import api from '../../utils/api';
import { Spinner, EmptyState, Modal, Badge, ConfirmDialog, useToast, requestBadgeMap, SearchableSelect, InlineSpinner } from '../../components/UI';
import { SectionIcon } from '../../components/StudentUI';
import FileAttachments from '../../components/FileAttachments';
import { fmtDate } from '../../utils/format';
import { REQUEST_TYPE_OPTIONS, DATE_RANGE_TYPES } from '../../utils/requestTypes';

const CANCELLABLE = new Set(['new', 'pending', 'in_review']);

const EMPTY_FORM = { request_type: '', description: '', priority: 'normal', start_date: '', end_date: '' };

export default function StudentRequestsPage() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [cancelId, setCancelId] = useState(null);
  const [viewing, setViewing] = useState(null);
  const attachRef = useRef();

  const load = () => {
    setLoading(true);
    api.get('/me/requests').then(r => setRows(r.data.data || []))
      .catch(() => toast('Failed to load your requests', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(EMPTY_FORM); setError(''); setShow(true); };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.request_type) { setError('Request type is required.'); return; }
    if (DATE_RANGE_TYPES.has(form.request_type)) {
      if (!form.start_date || !form.end_date) { setError('Start and end dates are required for this request type.'); return; }
      if (form.end_date < form.start_date) { setError('End date cannot be before start date.'); return; }
      const overlapping = rows.some(r =>
        r.status !== 'cancelled' && r.status !== 'rejected' && r.start_date && r.end_date &&
        r.start_date <= form.end_date && r.end_date >= form.start_date
      );
      if (overlapping) { setError('You already have a request covering part of this date range.'); return; }
      if (!attachRef.current?.hasQueued()) { setError('Please attach a supporting document (e.g. medical certificate).'); return; }
    }
    setSaving(true);
    try {
      const res = await api.post('/me/requests', form);
      await attachRef.current?.flush(res.data.id);
      toast('Request submitted', 'success');
      setShow(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Submit failed');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    try {
      await api.put(`/me/requests/${cancelId}/cancel`);
      toast('Request cancelled', 'success');
    } catch (err) {
      toast(err.response?.data?.message || 'Cancel failed', 'error');
    } finally {
      setCancelId(null);
      load();
    }
  };

  if (loading) return <div className="stu-page"><Spinner /></div>;

  return (
    <>
      <div className="stu-topbar">
        <div className="stu-topbar-title">My Requests</div>
        <div className="stu-topbar-sub">Submit a request and track its status</div>
      </div>
      <div className="stu-page">
        <div className="stu-card">
          <div className="stu-card-head">
            <h3 className="d-flex align-items-center"><SectionIcon icon="bi-envelope-paper" color="var(--sms-stat-amber)" />Requests</h3>
            <button className="sms-btn sms-btn-primary" onClick={openCreate}>
              <i className="bi bi-plus-lg" /> New Request
            </button>
          </div>
          <div className="stu-card-body">
            {rows.length === 0 ? (
              <EmptyState icon="bi-envelope-open" message="You haven't submitted any requests yet" />
            ) : (
              <div className="stu-req-grid">
                {rows.map(r => (
                  <div className="stu-req-tile" key={r.id}>
                    <div className="d-flex align-items-center justify-content-between" style={{ gap: 8 }}>
                      <div className="stu-activity-date" style={{ margin: 0, width: 'auto' }}>
                        <i className="bi bi-calendar3" style={{ marginRight: 4 }} />{fmtDate(r.created_at)}
                      </div>
                      <Badge value={r.status} map={requestBadgeMap} />
                    </div>
                    <div className="stu-activity-cat" style={{ marginTop: 8 }}>
                      <i className="bi bi-file-earmark-text" style={{ marginRight: 5 }} />
                      {r.request_type}
                      {r.priority === 'urgent' && <i className="bi bi-exclamation-triangle-fill" style={{ marginLeft: 6, color: 'var(--sms-stat-red)', fontSize: 12 }} title="Urgent" />}
                    </div>
                    {r.start_date && (
                      <div className="stu-activity-desc" style={{ color: 'var(--sms-txt2)' }}>
                        <i className="bi bi-calendar-event" style={{ marginRight: 5, color: 'var(--sms-txt3)' }} />
                        Leave: {fmtDate(r.start_date)} – {fmtDate(r.end_date)}
                      </div>
                    )}
                    {r.description && (
                      <div className="stu-activity-desc stu-req-clamp">
                        <i className="bi bi-chat-left-text" style={{ marginRight: 5, color: 'var(--sms-txt3)' }} />{r.description}
                      </div>
                    )}
                    <div className="d-flex align-items-center justify-content-between mt-2" style={{ gap: 8 }}>
                      <button type="button" className="sms-btn sms-btn-outline" style={{ fontSize: 12, padding: '3px 8px' }} onClick={() => setViewing(r)}>
                        <i className="bi bi-eye" /> View details
                      </button>
                      {CANCELLABLE.has(r.status) && r.source === 'student' && (
                        <button type="button" className="sms-btn sms-btn-outline-danger" style={{ fontSize: 12, padding: '3px 8px' }} onClick={() => setCancelId(r.id)}>
                          <i className="bi bi-x-circle" /> Cancel
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal show={show} onClose={() => setShow(false)} title="New Request" size="lg">
        <form onSubmit={handleSubmit} noValidate>
          {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}
          <div className="row g-3">
            <div className="col-12 col-sm-6">
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}><i className="bi bi-file-earmark-text" style={{ marginRight: 5 }} />Request Type *</label>
              <SearchableSelect value={form.request_type} onChange={e => setForm(f => ({ ...f, request_type: e.target.value }))}
                placeholder="-- Select request type --" options={REQUEST_TYPE_OPTIONS} />
            </div>
            <div className="col-12 col-sm-6">
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}><i className="bi bi-flag" style={{ marginRight: 5 }} />Priority</label>
              <select className="sms-form-control" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                <option value="normal">Normal</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            {DATE_RANGE_TYPES.has(form.request_type) && (
              <>
                <div className="col-12 col-sm-6">
                  <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}><i className="bi bi-calendar-event" style={{ marginRight: 5 }} />Start Date *</label>
                  <input type="date" className="sms-form-control" value={form.start_date}
                    onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
                </div>
                <div className="col-12 col-sm-6">
                  <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}><i className="bi bi-calendar-event" style={{ marginRight: 5 }} />End Date *</label>
                  <input type="date" className="sms-form-control" value={form.end_date} min={form.start_date || undefined}
                    onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} />
                </div>
              </>
            )}
            <div className="col-12">
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}><i className="bi bi-chat-left-text" style={{ marginRight: 5 }} />Description</label>
              <textarea className="sms-form-control" rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Describe your request" />
            </div>
          </div>
          <div className="mt-3">
            <FileAttachments ref={attachRef} module="requests" recordId={null} canUpload canDelete={false} />
          </div>
          <div className="d-flex gap-2 justify-content-end mt-3">
            <button type="button" className="sms-btn sms-btn-outline" onClick={() => setShow(false)}><i className="bi bi-x-lg" /> Cancel</button>
            <button type="submit" className="sms-btn sms-btn-primary" disabled={saving}>
              {saving ? <InlineSpinner /> : <><i className="bi bi-send" /> Submit</>}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog show={!!cancelId} danger message="Cancel this request? This cannot be undone." onConfirm={handleCancel} onCancel={() => setCancelId(null)} />

      <Modal show={!!viewing} onClose={() => setViewing(null)} title="Request Details" size="lg">
        {viewing && (
          <div>
            <div className="d-flex align-items-center justify-content-between mb-3" style={{ flexWrap: 'wrap', gap: 8 }}>
              <div className="stu-activity-cat" style={{ fontSize: 15 }}>
                <i className="bi bi-file-earmark-text" style={{ marginRight: 5 }} />
                {viewing.request_type}
                {viewing.priority === 'urgent' && <i className="bi bi-exclamation-triangle-fill" style={{ marginLeft: 6, color: 'var(--sms-stat-red)', fontSize: 12 }} title="Urgent" />}
              </div>
              <Badge value={viewing.status} map={requestBadgeMap} />
            </div>
            <div className="stu-activity-date" style={{ margin: '0 0 10px', width: 'auto' }}>
              <i className="bi bi-calendar3" style={{ marginRight: 4 }} />Submitted {fmtDate(viewing.created_at)}
            </div>
            {viewing.start_date && (
              <div className="stu-activity-desc" style={{ color: 'var(--sms-txt2)' }}>
                <i className="bi bi-calendar-event" style={{ marginRight: 5, color: 'var(--sms-txt3)' }} />
                Leave: {fmtDate(viewing.start_date)} – {fmtDate(viewing.end_date)}
              </div>
            )}
            {viewing.description && (
              <div className="stu-activity-desc" style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>
                <i className="bi bi-chat-left-text" style={{ marginRight: 5, color: 'var(--sms-txt3)' }} />{viewing.description}
              </div>
            )}
            {viewing.remarks && (
              <div className="stu-activity-desc" style={{ marginTop: 6, color: 'var(--sms-txt2)', whiteSpace: 'pre-wrap' }}>
                <i className="bi bi-chat-square-quote" style={{ marginRight: 5 }} /><strong>Remarks:</strong> {viewing.remarks}
              </div>
            )}
            <div className="mt-3">
              <FileAttachments module="requests" recordId={viewing.id} canUpload={false} canDelete={false} />
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
